/**
 * useGoogleMaps — lazy-load the Google Maps JS SDK once per app session.
 *
 * The hook fetches the *browser-safe* config from
 * ``/api/public/settings/google-maps-config`` (which never returns the
 * server-side key), then injects the Maps JS bundle into ``<head>`` a
 * single time. Every downstream consumer (AddressAutocomplete,
 * SiteInsightsModal, future map widgets) shares the same singleton
 * loader promise, so we never race the SDK.
 *
 * Fallback: when Google Maps isn't configured — or the browser key is
 * missing — the hook resolves ``ready = false`` and callers fall back
 * to plain inputs / OSM tiles.
 */
import { useEffect, useState } from 'react';
import { api } from './api';

interface GoogleMapsPublicConfig {
  enabled: boolean;
  browser_key: string;
  libraries: string[];
  region: string;
}

interface UseGoogleMapsResult {
  ready: boolean;
  enabled: boolean;
  loading: boolean;
  error: string | null;
  region: string;
}

/* ─── Module-level singletons ──────────────────────────────────────
 * We want ONE fetch of the config + ONE script injection per page
 * load, regardless of how many components mount the hook. Storing
 * these outside the component body means React strict-mode double
 * mounting doesn't produce duplicate <script> tags. */
let configPromise: Promise<GoogleMapsPublicConfig> | null = null;
let loaderPromise: Promise<void> | null = null;
let cachedConfig: GoogleMapsPublicConfig | null = null;

/** Clear the cached config so the next consumer refetches the browser
 * key for the current tenant. Called on login / tenant-switch / logout
 * so a Master Admin switching insurers gets the right key. */
export function resetGoogleMaps(): void {
  configPromise = null;
  cachedConfig = null;
}

/** Return (and cache) the browser-safe config from the backend. The
 * request goes through the shared ``api`` instance so it carries the
 * caller's bearer token — the backend then serves THIS tenant's key. */
function fetchConfig(): Promise<GoogleMapsPublicConfig> {
  if (configPromise) return configPromise;
  configPromise = api
    .get<GoogleMapsPublicConfig>(`/public/settings/google-maps-config`)
    .then((r) => {
      cachedConfig = r.data;
      return r.data;
    })
    .catch(() => {
      // Treat any failure as "maps disabled" — the app degrades
      // gracefully to plain inputs + OSM tiles.
      const disabled: GoogleMapsPublicConfig = {
        enabled: false, browser_key: '', libraries: ['places'], region: 'za',
      };
      cachedConfig = disabled;
      return disabled;
    });
  return configPromise;
}

/** Resolve once ``google.maps.importLibrary`` actually exists.
 *
 * With ``loading=async`` the <script> ``onload`` event fires BEFORE the
 * bootstrap finishes wiring up ``importLibrary`` and the service classes
 * (StreetViewService, places.Autocomplete, …). Resolving on ``onload``
 * therefore reports "ready" while the SDK is still half-initialised —
 * which is exactly what caused Street View to fail on the FIRST open and
 * only work on the second (by then the bootstrap had caught up). */
function waitForImportLibrary(timeoutMs = 10000): Promise<void> {
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const tick = (): void => {
      const g = (window as unknown as { google?: { maps?: { importLibrary?: unknown } } }).google;
      if (typeof g?.maps?.importLibrary === 'function') { resolve(); return; }
      if (Date.now() - start > timeoutMs) { reject(new Error('Google Maps importLibrary never became available')); return; }
      setTimeout(tick, 50);
    };
    tick();
  });
}

/** Inject <script src=maps.googleapis.com/maps/api/js> exactly once. */
function loadScript(cfg: GoogleMapsPublicConfig): Promise<void> {
  if (loaderPromise) return loaderPromise;
  if (!cfg.enabled || !cfg.browser_key) {
    loaderPromise = Promise.reject(new Error('Google Maps not enabled'));
    return loaderPromise;
  }
  // If the SDK is already present (e.g. hot-reloaded), reuse it.
  const w = window as unknown as { google?: { maps?: { places?: unknown } } };
  if (w.google?.maps?.places) {
    loaderPromise = Promise.resolve();
    return loaderPromise;
  }
  loaderPromise = new Promise<void>((resolve, reject) => {
    const libs = (cfg.libraries || ['places']).join(',');
    const src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(cfg.browser_key)}&libraries=${libs}&loading=async&region=${encodeURIComponent(cfg.region || 'za').toUpperCase()}&v=weekly`;
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.defer = true;
    s.onerror = () => reject(new Error('Failed to load Google Maps JS SDK'));
    s.onload = () => {
      // onload != ready under loading=async. Wait for importLibrary, then
      // eagerly import the libraries we actually use so that by the time
      // callers see ready=true the SDK is FULLY usable (populates the
      // google.maps.places global that AddressAutocomplete relies on).
      void (async () => {
        try {
          await waitForImportLibrary();
          const g = (window as unknown as { google: { maps: { importLibrary: (n: string) => Promise<unknown> } } }).google;
          await g.maps.importLibrary('places');
          resolve();
        } catch (e) { reject(e as Error); }
      })();
    };
    document.head.appendChild(s);
  });
  return loaderPromise;
}

export function useGoogleMaps(): UseGoogleMapsResult {
  const [state, setState] = useState<UseGoogleMapsResult>(() => ({
    ready: false,
    enabled: cachedConfig?.enabled ?? false,
    loading: true,
    error: null,
    region: cachedConfig?.region ?? 'za',
  }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await fetchConfig();
        if (cancelled) return;
        if (!cfg.enabled || !cfg.browser_key) {
          setState({ ready: false, enabled: false, loading: false, error: null, region: cfg.region });
          return;
        }
        setState((s) => ({ ...s, enabled: true, region: cfg.region }));
        await loadScript(cfg);
        if (cancelled) return;
        setState({ ready: true, enabled: true, loading: false, error: null, region: cfg.region });
      } catch (err) {
        if (cancelled) return;
        setState({ ready: false, enabled: cachedConfig?.enabled ?? false, loading: false, error: (err as Error).message, region: cachedConfig?.region ?? 'za' });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return state;
}

/** Structured place data emitted by AddressAutocomplete. */
export interface ResolvedPlace {
  formatted_address: string;
  place_id: string;
  lat: number;
  lng: number;
  city?: string;
  province?: string;
  postal_code?: string;
  country?: string;
}

/** Convert a google.maps.places.PlaceResult to our simpler shape. */
export function extractPlace(place: unknown): ResolvedPlace | null {
  const p = place as { geometry?: { location?: { lat: (() => number) | number; lng: (() => number) | number } }; formatted_address?: string; place_id?: string; name?: string; address_components?: Array<{ long_name: string; short_name: string; types: string[] }> };
  if (!p || !p.geometry?.location) return null;
  const comps = p.address_components || [];
  const find = (type: string): string | undefined => comps.find((c) => c.types.includes(type))?.long_name;
  const loc = p.geometry.location;
  return {
    formatted_address: p.formatted_address || p.name || '',
    place_id: p.place_id || '',
    lat: typeof loc.lat === 'function' ? loc.lat() : loc.lat,
    lng: typeof loc.lng === 'function' ? loc.lng() : loc.lng,
    city: find('locality') || find('sublocality') || find('postal_town'),
    province: find('administrative_area_level_1'),
    postal_code: find('postal_code'),
    country: find('country'),
  };
}
